import { useMemo } from 'react'
import { PILL } from '@/features/fraud/format'
import { discountErrorMessage } from '@/lib/api/discount-client'
import { useItemDiscounts } from '@/lib/api/discount-queries'
import type { DiscountWindowType, ItemDiscountRow } from '@/lib/api/discount-types'
import { cn } from '@/lib/utils'
import { formatUntil, hasPassed, useEndsClock, useRouteEvent } from './menu-price'
import { formatCurrency, formatMenuPrice, menuUnits, type MenuUnits } from './money'

const WINDOW_LABEL: Record<DiscountWindowType, string> = { one_off: 'One-off', recurring: 'Recurring' }

// The one charging first, then the ones open now, then the rest; excluded last.
function rank(row: ItemDiscountRow): number {
  if (row.active) return 0
  if (row.price != null) return 1
  return row.excluded ? 3 : 2
}

// As the item modal prints a price: currency on a Direct Pay event, else
// tokens with their currency value.
function Price({ price, units }: { price: number; units: MenuUnits }) {
  if (units.eventType === 'direct_pay') return <>{formatMenuPrice(price, units)}</>
  return (
    <>
      {formatMenuPrice(price, units)}{' '}
      {/* The menu's `token_value || 2.0`. */}
      <span className="font-normal text-neutral-400">({formatCurrency(price * (units.tokenValue ?? 2), units.currency)})</span>
    </>
  )
}

/** Every live schedule that targets the item, and which one is charging it now. Read-only. */
export function ItemDiscountsPanel({ itemId }: { itemId: number }) {
  const event = useRouteEvent()
  const units = useMemo(() => menuUnits(event ?? {}), [event])
  const { data, isLoading, error } = useItemDiscounts(itemId)
  // Nothing is discounted while the event's promotions are switched off,
  // whatever the rows say.
  const promotionsOff = event?.enable_discount_schedules === false
  // A window that has closed since the fetch is not open any more.
  const now = useEndsClock(data?.schedules.map((row) => row.ends_at) ?? [])
  const rows = useMemo(
    () => (data?.schedules ?? [])
      .map((row): ItemDiscountRow => (hasPassed(row.ends_at, now) ? { ...row, price: null, active: false, ends_at: null } : row))
      .sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name)),
    [data, now],
  )

  return (
    <section className="space-y-3 border-t border-neutral-200 dark:border-neutral-800 pt-6">
      <div>
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Promotions</h3>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
          Schedules that target this item. Paused and archived schedules are not listed.
        </p>
      </div>

      {error ? (
        <p className="text-sm text-red-600 dark:text-red-400">
          Could not load promotions: {discountErrorMessage(error)}
        </p>
      ) : isLoading ? (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Loading promotions...</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">No promotions target this item</p>
      ) : (
        <>
          {promotionsOff && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Promotions are switched off for this event, so none of these is charging.
            </p>
          )}
          <ul className="divide-y divide-neutral-200 dark:divide-neutral-800 rounded-md border border-neutral-200 dark:border-neutral-800">
            {rows.map((row) => {
              const charging = row.active && !promotionsOff
              return (
                <li key={row.id} className={cn('flex items-start justify-between gap-3 px-3 py-2', charging && 'bg-emerald-50/60 dark:bg-emerald-900/10')}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100 break-words">
                      {row.name || `Schedule #${row.id}`}
                    </div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">
                      {WINDOW_LABEL[row.window_type] ?? row.window_type}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 text-right shrink-0">
                    {row.excluded ? (
                      <span className={cn(PILL, 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300')}>Excluded</span>
                    ) : row.price != null ? (
                      <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100 whitespace-nowrap">
                        <Price price={row.price} units={units} />
                      </span>
                    ) : (
                      <span className="text-xs text-neutral-500 dark:text-neutral-400">Not running now</span>
                    )}
                    {charging ? (
                      <span className="flex items-center gap-1.5 whitespace-nowrap">
                        <span className={cn(PILL, 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/25 dark:text-emerald-300')}>Active now</span>
                        {row.ends_at && (
                          <span className="text-xs text-neutral-500 dark:text-neutral-400">
                            until {formatUntil(row.ends_at, event?.timezone)}
                          </span>
                        )}
                      </span>
                    ) : !promotionsOff && !row.excluded && row.price != null ? (
                      <span className="text-xs text-neutral-500 dark:text-neutral-400">Open now, another price applies</span>
                    ) : null}
                  </div>
                </li>
              )
            })}
          </ul>
        </>
      )}
    </section>
  )
}
